import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { FileText, AlertTriangle, Scale, CreditCard, Phone, Mail } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Terms and Conditions"
        canonicalUrl="/terms"
        noIndex
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Terms and Conditions" }])}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-wl-blue/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-wl-blue" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Whitefield Legal Services Ltd Terms and Conditions
          </h1>
          <p className="text-xl text-white/90">
            These terms govern your use of our services.
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last updated: July 2025
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Agreement to Terms</h2>
            <div className="bg-wl-light p-6 rounded-xl">
              <p className="text-wl-gray mb-4">
               WLS Ltd supplies conveyancing searches for its clients on the conditions set out below. These
               conditions cannot be varied unless agreed in writing by a Director of WLS Ltd.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-wl-blue">
                <p className="text-sm text-wl-gray">
                  <strong className="text-wl-navy">Company Details:</strong> Whitefield Legal Services Limited, 
                  Company Number 06043320, registered at 2nd Floor Argyle House, Joel Street, Northwood Hills, HA6 1NW.
                </p>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-green/10 rounded-lg flex items-center justify-center mr-4">
                <FileText className="w-5 h-5 text-wl-green" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Our Services</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Property Searches</h3>
                <ul className="text-wl-gray text-sm space-y-2">
                  <li>• Local Authority searches</li>
                  <li>• Environmental reports</li>
                  <li>• Water and drainage searches</li>
                  <li>• Specialist risk assessments</li>
                </ul>
              </div>
              
              <div className="border border-gray-200 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Business Services</h3>
                <ul className="text-wl-gray text-sm space-y-2">
                  <li>• IT solutions and platforms</li>
                  <li>• Compliance services</li>
                  <li>• Tailored search packages</li>
                  <li>• Insurance services</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Full Terms and Conditions */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-blue/10 rounded-lg flex items-center justify-center mr-4">
                <CreditCard className="w-5 h-5 text-wl-blue" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Full Terms and Conditions</h2>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Acceptance of orders</h3>
                <p className="text-wl-gray text-sm mb-2">
                We are not obliged to accept any order and we may refuse to provide
                the services requested at any time without giving reason. There is no contract for services to be
                provided until the order has been accepted.Each order accepted is a new and separate
                contract.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Accuracy</h3>
              <p className="text-wl-gray text-sm mb-2">
                We will use reasonable endeavours to ensure that the information contained within
                any report is accurate at the date of its publication. You (the client) accept, however, that
                information on which the report is based may be subject to change from the date of its
                publication and we cannot be held liable for failing to include or omit information in the report,
                which becomes publicly available after the date of the publication.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Timescale</h3>
                <p className="text-wl-gray text-sm mb-2">
                Any indication that we may give as to the time in which we will deliver the report
                will be a good faith estimate only. We will use all reasonable endeavours to deliver the reports
                within the time-scale that we have estimated.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Charges</h3>
                <p className="text-wl-gray text-sm mb-2">
                Unless stated otherwise, all charges will include VAT at the appropriate rate
              </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Payment</h3>
                <p className="text-wl-gray text-sm mb-2">
                You will receive a VAT invoice as soon as is practicable after the completion of the
                report. You will pay the invoice in full within 30 days of its issue date. Failure to pay will result in
                reporting the matter to the appropriate trade bodies and accreditation authority. Failure may
                also result in our refusal to accept any further orders from you.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Your Obligations</h3>
                <p className="text-wl-gray text-sm mb-2">
                You are obliged to provide full, accurate and up to date information with
                your order. Your order should also be accompanied by a recent, legible and accurate plan of the
                property being the subject of the report. You agree that the report supplied is only for your use.
                Any client of yours receiving one of our reports is also bound by these conditions.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Cancellation</h3>
                <p className="text-wl-gray text-sm mb-2">
                Should you wish to cancel or re-schedule an order, you must give as much
                notice as possible. If an appointment has already been made, we will endeavour to cancel the
                appointment at no cost to either party. We may have to pay the disbursement, which will be
                passed onto you. If the request to cancel is not received until the same working day as the
                appointment, the search will be carried out in full, and invoiced as such.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Intellectual Property Rights</h3>
                <p className="text-wl-gray text-sm mb-2">
                All Intellectual Property Rights in the reports remain our
                property
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Limitation of Liability</h3>
                <p className="text-wl-gray text-sm mb-2">
                We will not accept liability for errors in reports where that error is
                caused by the inaccuracy of information held in a public register or public domain. We will not
                be responsible for errors in the registration of information. We will not be held liable for errors
                caused by inaccurate information of documentation supplied by you at any time in the report
                process. We will not be held liable for delays caused by unforeseen circumstances. Our entire
                liability for negligence will not exceed £2 million. However, insurance provided with WLS Ltd
                searches provides cover for errors and omissions in local authority data and records.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Force Majeure</h3>
                <p className="text-wl-gray text-sm mb-2">
                We will not be held liable for any failure to perform the service due to an
                event beyond our reasonable control, we will notify you promptly of the reason for any delay,
                and you agree to allow us an extension to perform the service as is reasonably practicable.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Quality of Service</h3>
                <p className="text-wl-gray text-sm mb-2">
                We agree to ensure that all services provided by WLS Searches Ltd are
                carried out in professional manner. All sta involved in the services we provided shall be
                competent and fully trained. All sta of WLS shall deal with you in a polite and helpful manner.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Independant Dispute Resolution</h3>
                <p className="text-wl-gray text-sm mb-2">
                If you make a complaint and we are unable to resolve it to
                your satisfaction you may refer the complaint to The Property Ombudsman scheme (web site
                www.tpos.co.uk , email: admin@tpos.co.uk ). We will co-operate fully with the Ombudsman
                during an investigation and comply with the Ombudsman’s final decision.
                </p>
              </div>
            </div>
          </div>

          {/* Liability & Insurance */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-navy/10 rounded-lg flex items-center justify-center mr-4">
                <Scale className="w-5 h-5 text-wl-navy" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Liability & Insurance</h2>
            </div>
            
            <div className="bg-wl-light p-6 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-wl-navy mb-3">Professional Indemnity</h3>
                  <p className="text-wl-gray text-sm mb-2">
                    We maintain £2 million professional indemnity insurance with leading insurers.
                  </p>
                  <p className="text-wl-gray text-sm">
                    Coverage details available upon request.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-wl-navy mb-3">Limitation of Liability</h3>
                  <p className="text-wl-gray text-sm mb-2">
                    Our liability is limited to the amount paid for the specific service.
                  </p>
                  <p className="text-wl-gray text-sm">
                    Excludes indirect, consequential, or special damages.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance & Standards */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Compliance & Professional Standards</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-wl-blue/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-wl-blue" />
                </div>
                <h4 className="font-semibold text-wl-navy mb-2">PCCB Registered</h4>
                <p className="text-wl-gray text-sm">Property Codes Compliance Board subscriber</p>
              </div>
              
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-wl-green/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Scale className="w-6 h-6 text-wl-green" />
                </div>
                <h4 className="font-semibold text-wl-navy mb-2">IPSA Member</h4>
                <p className="text-wl-gray text-sm">15 years membership with training and support</p>
              </div>
              
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-wl-navy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-6 h-6 text-wl-navy" />
                </div>
                <h4 className="font-semibold text-wl-navy mb-2">TPO Scheme</h4>
                <p className="text-wl-gray text-sm">Independent dispute resolution available</p>
              </div>
            </div>
          </div>

          {/* Complaints Procedure */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-green/10 rounded-lg flex items-center justify-center mr-4">
                <AlertTriangle className="w-5 h-5 text-wl-green" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Complaints Procedure</h2>
            </div>
            <div className="space-y-6">
              {/* Consumer Information */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Consumer Information with Complaints Procedure</h3>
                <p className="text-wl-gray text-sm mb-2">
                  This search has been produced by WHITEFIELD LEGAL SERVICES, Argyle House, Joel Street, Northwood Hills, Middx, HA6 1NW. Tel: 01923 620240, Email: admin@whitefieldlegal.com, which is registered with the Property Codes Compliance Board (PCCB) as a subscriber to the Search Code. The PCCB independently monitors how registered search firms maintain compliance with the Code.
                </p>
              </div>
              {/* The Search Code */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">The Search Code</h3>
                <ul className="list-disc list-inside text-wl-gray text-sm space-y-1">
                  <li>Provides protection for homebuyers, sellers, estate agents, conveyancers and mortgage lenders who rely on the information included in property search reports undertaken by subscribers on residential and commercial property within the United Kingdom</li>
                  <li>Sets out minimum standards which firms compiling and selling search reports have to meet</li>
                  <li>Promotes best practice and quality standards within the industry for the benefit of consumers and property professionals</li>
                  <li>Enables consumers and property professionals to have confidence in firms which subscribe to the code, their products and services</li>
                </ul>
                <p className="text-wl-gray text-sm mt-2">
                  By giving you this information, the search firm is confirming that they keep to the principles of the Code. This provides important protection for you.
                </p>
              </div>
              {/* The Code's Core Principles */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">The Code’s Core Principles</h3>
                <ul className="list-disc list-inside text-wl-gray text-sm space-y-1">
                  <li>Display the Search Code logo prominently on their search reports</li>
                  <li>Act with integrity and carry out work with due skill, care and diligence</li>
                  <li>At all times maintain adequate and appropriate insurance to protect consumers</li>
                  <li>Conduct business in an honest, fair and professional manner</li>
                  <li>Handle complaints speedily and fairly</li>
                  <li>Ensure that products and services comply with industry registration rules and standards and relevant laws</li>
                  <li>Monitor their compliance with the Code</li>
                </ul>
              </div>
              {/* Complaints */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Complaints</h3>
                <p className="text-wl-gray text-sm mb-2">
                  If you have a query or complaint about your search, you should raise it directly with the search firm, and if appropriate ask for any complaint to be considered under their formal internal complaints procedure.
                </p>
              </div>
              {/* Independent Dispute Resolution */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Independent Dispute Resolution</h3>
                <p className="text-wl-gray text-sm mb-2">
                  If you make a complaint and we are unable to resolve it to your satisfaction you may refer the complaint to The Property Ombudsman scheme (website: <a href="https://www.tpos.co.uk" className="underline text-wl-blue" target="_blank" rel="noopener noreferrer">www.tpos.co.uk</a>, email: <a href="mailto:admin@tpos.co.uk" className="underline text-wl-blue">admin@tpos.co.uk</a>). We will co-operate fully with the Ombudsman during an investigation and comply with the Ombudsman’s final decision. The Ombudsman can award up to £5,000 to you if the Ombudsman finds that you have suffered actual financial loss and/or aggravation, distress or inconvenience as a result of your search provider failing to keep to the Code.
                </p>
                <p className="text-wl-gray text-sm">
                  Please note that all queries or complaints regarding your search should be directed to your search provider in the first instance, not to TPOs or to the PCCB.
                </p>
              </div>
              {/* TPO Contact Details */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">TPO Contact Details</h3>
                <p className="text-wl-gray text-sm mb-2">
                  The Property Ombudsman scheme<br />
                  Milford House, 43-55 Milford Street<br />
                  Salisbury, Wiltshire SP1 2BP<br />
                  Tel: 01722 333306<br />
                  Fax: 01722 332296<br />
                  Website: <a href="https://www.tpos.co.uk" className="underline text-wl-blue" target="_blank" rel="noopener noreferrer">www.tpos.co.uk</a><br />
                  Email: <a href="mailto:admin@tpos.co.uk" className="underline text-wl-blue">admin@tpos.co.uk</a>
                </p>
                <p className="text-wl-gray text-sm">
                  You can get more information about the PCCB from <a href="https://www.propertycodes.org.uk" className="underline text-wl-blue" target="_blank" rel="noopener noreferrer">www.propertycodes.org.uk</a>.
                </p>
              </div>
              {/* Request a Copy of the Search Code */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Request a Copy of the Search Code</h3>
                <p className="text-wl-gray text-sm mb-2">
                  Please ask your search provider if you would like a copy of the Search Code.
                </p>
              </div>
              {/* Whitefield Legal Services Complaints Procedure */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Whitefield Legal Services Complaints Procedure</h3>
                <p className="text-wl-gray text-sm mb-2">
                  Whitefield Legal Services is registered with the Property Codes Compliance Board as a subscriber to the Search Code. A key commitment under the Code is that firms will handle any complaints both speedily and fairly. If you want to make a complaint, we will:
                </p>
                <ul className="list-disc list-inside text-wl-gray text-sm space-y-1">
                  <li><a href="/contact" className="underline text-wl-blue" rel="noopener noreferrer">Contact</a> us by email or telephone to discuss your complaint.</li>
                  <li>Acknowledge it within 5 working days of receipt.</li>
                  <li>Normally deal with it fully and provide a final response, in writing, within 20 working days of receipt.</li>
                  <li>Keep you informed by letter, telephone or e-mail, as you prefer, if we need more time.</li>
                  <li>Provide a final response, in writing, at the latest within 40 working days of receipt.</li>
                  <li>Liaise, at your request, with anyone acting formally on your behalf.</li>
                </ul>
                <p className="text-wl-gray text-sm mt-2">
                  If you are not satisfied with our final response, or if we exceed the response timescales, you may refer the complaint to The Property Ombudsman scheme (TPOs): Tel: 01722 333306, Website: <a href="https://www.tpos.co.uk" className="underline text-wl-blue" target="_blank" rel="noopener noreferrer">www.tpos.co.uk</a>, Email: <a href="mailto:admin@tpos.co.uk" className="underline text-wl-blue">admin@tpos.co.uk</a>. We will co-operate fully with the Ombudsman during an investigation and comply with his final decision.
                </p>
              </div>
            </div>
          </div>

          {/* General Terms */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">General Terms</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-wl-blue pl-4">
                <h4 className="font-semibold text-wl-navy mb-1">Governing Law</h4>
                <p className="text-wl-gray text-sm">These terms are governed by English law and subject to the jurisdiction of English courts.</p>
              </div>
              
              <div className="border-l-4 border-wl-green pl-4">
                <h4 className="font-semibold text-wl-navy mb-1">Changes to Terms</h4>
                <p className="text-wl-gray text-sm">We may update these terms from time to time. Changes will be posted on our website.</p>
              </div>
              
              <div className="border-l-4 border-wl-navy pl-4">
                <h4 className="font-semibold text-wl-navy mb-1">Severability</h4>
                <p className="text-wl-gray text-sm">If any provision is deemed invalid, the remaining provisions will continue in full force.</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <h2 className="text-xl font-bold text-wl-navy mb-4">Questions About These Terms?</h2>
            <p className="text-wl-gray mb-4">Contact us for clarification on any aspect of our terms of service:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center text-wl-navy">
                <Mail className="w-4 h-4 mr-2" />
                <span>admin@whitefieldlegal.com</span>
              </div>
              <div className="flex items-center text-wl-navy">
                <Phone className="w-4 h-4 mr-2" />
                <span>01923 620240/41</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 